import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FIELD_META, type AgentPromptField } from '@cartcloser/prompts';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Textarea,
} from '@cartcloser/ui';
import { superGetTemplate, superSetTemplateField } from '../lib/api.js';

export const Route = createFileRoute('/super/templates/$id')({
  component: TemplateEditor,
});

function TemplateEditor() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['super-template', id],
    queryFn: () => superGetTemplate(id),
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (query.isError || !query.data)
    return (
      <Alert variant="destructive">
        <AlertDescription>Couldn’t load this template.</AlertDescription>
      </Alert>
    );

  const { template, fields } = query.data;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/super/templates" className="text-sm text-primary hover:underline">
          ← Templates
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">{template.name}</h1>
        <p className="text-sm text-muted-foreground">{template.templateKey}</p>
      </div>

      {fields.map((f) => (
        <TemplateFieldEditor
          key={f.field}
          templateId={id}
          field={f.field as AgentPromptField}
          initialContent={f.content}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['super-template', id] })}
        />
      ))}
    </div>
  );
}

function TemplateFieldEditor({
  templateId,
  field,
  initialContent,
  onSaved,
}: {
  templateId: string;
  field: AgentPromptField;
  initialContent: string;
  onSaved: () => void;
}) {
  const meta = FIELD_META[field];
  const [content, setContent] = useState(initialContent);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => superSetTemplateField(templateId, field, content),
    onSuccess: () => {
      setSaved(true);
      onSaved();
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{meta.label}</CardTitle>
        <CardDescription>{meta.hint}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Textarea
          rows={5}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            setSaved(false);
          }}
          placeholder="Proprietary prompt text…"
        />
        <div className="flex items-center gap-3">
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
          {saved ? <span className="text-xs text-primary">Saved</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}
